// ═══════════════════════════════════════════════════════════════════════════════
// Admin shell — responsive sidebar (drawer on small screens) + main
// ═══════════════════════════════════════════════════════════════════════════════

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ADMIN_ROLE, LANGUAGE } from '@fdh/shared';
import { adminPortalPath } from '../../lib/adminPortalPaths';

export const AdminLayout = () => {
  const { admin, logout } = useAdminAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const isChineseUi =
    language === LANGUAGE.ZH_HANT || language === LANGUAGE.ZH_HANS;
  const isEnglishUi = language === LANGUAGE.EN;

  const handleLogout = useCallback(async () => {
    await logout();
    navigate(adminPortalPath('login'), { replace: true });
  }, [logout, navigate]);

  const handleCloseMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleToggleMenu = useCallback(() => {
    setMenuOpen((open) => !open);
  }, []);

  const handleSelectChinese = useCallback(() => {
    setLanguage(LANGUAGE.ZH_HANT);
  }, [setLanguage]);

  const handleSelectEnglish = useCallback(() => {
    setLanguage(LANGUAGE.EN);
  }, [setLanguage]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  const isSuper = admin?.role === ADMIN_ROLE.SUPER_ADMIN;

  const navClass = ({ isActive }: { isActive: boolean }): string =>
    [
      'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
      isActive
        ? 'bg-slate-800 text-white'
        : 'text-slate-300 hover:bg-slate-800/80 hover:text-white',
    ].join(' ');

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100">
      {menuOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          aria-label={t('admin.closeMenu')}
          onClick={handleCloseMenu}
        />
      ) : null}

      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-64 max-w-[85vw] flex-col border-r border-slate-800 bg-slate-900 px-3 py-6 shadow-xl transition-transform duration-200 ease-out md:static md:z-0 md:w-56 md:max-w-none md:translate-x-0 md:shadow-none',
          menuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        ].join(' ')}
        aria-label={t('admin.sidebarAria')}
        id="admin-sidebar"
      >
        <div className="mb-6 flex items-start justify-between gap-2 px-2 md:mb-8">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-400/90">
              {t('admin.brand')}
            </p>
            <p className="mt-1 truncate text-xs text-slate-400">{admin?.email}</p>
          </div>
          <button
            type="button"
            className="rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white md:hidden"
            onClick={handleCloseMenu}
            aria-label={t('admin.closeMenu')}
            tabIndex={0}
          >
            <span aria-hidden="true" className="block text-lg leading-none">
              ×
            </span>
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
          <NavLink to={adminPortalPath('dashboard')} className={navClass} end onClick={handleCloseMenu}>
            {t('admin.nav.dashboard')}
          </NavLink>
          <NavLink to={adminPortalPath('users')} className={navClass} onClick={handleCloseMenu}>
            {t('admin.nav.users')}
          </NavLink>
          <NavLink to={adminPortalPath('settings')} className={navClass} onClick={handleCloseMenu}>
            {t('admin.nav.settings')}
          </NavLink>
          <NavLink
            to={adminPortalPath('invitation-codes')}
            className={navClass}
            onClick={handleCloseMenu}
          >
            {t('admin.nav.invitationCodes')}
          </NavLink>
          {isSuper ? (
            <NavLink
              to={adminPortalPath('system-messages')}
              className={navClass}
              onClick={handleCloseMenu}
            >
              {t('admin.nav.systemMessages')}
            </NavLink>
          ) : null}
          <NavLink
            to={adminPortalPath('legal-documents')}
            className={navClass}
            onClick={handleCloseMenu}
          >
            {t('admin.nav.legalDocuments')}
          </NavLink>
          <NavLink to={adminPortalPath('faqs')} className={navClass} onClick={handleCloseMenu}>
            {t('admin.nav.faqs')}
          </NavLink>
          <NavLink to={adminPortalPath('audit-logs')} className={navClass} onClick={handleCloseMenu}>
            {t('admin.nav.audit')}
          </NavLink>
          {isSuper ? (
            <NavLink to={adminPortalPath('admins')} className={navClass} onClick={handleCloseMenu}>
              {t('admin.nav.admins')}
            </NavLink>
          ) : null}
        </nav>
        <div className="mt-auto flex flex-col gap-2 border-t border-slate-800 pt-4">
          <div
            className="flex overflow-hidden rounded-md border border-slate-700"
            role="group"
            aria-label={t('admin.languageSwitcherAria')}
          >
            <button
              type="button"
              className={[
                'flex-1 px-3 py-2 text-sm font-medium transition-colors',
                isChineseUi
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white',
              ].join(' ')}
              onClick={handleSelectChinese}
              aria-label={t('admin.languageChinese')}
              aria-pressed={isChineseUi}
              tabIndex={0}
            >
              中
            </button>
            <button
              type="button"
              className={[
                'flex-1 border-l border-slate-700 px-3 py-2 text-sm font-medium transition-colors',
                isEnglishUi
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white',
              ].join(' ')}
              onClick={handleSelectEnglish}
              aria-label={t('admin.languageEnglish')}
              aria-pressed={isEnglishUi}
              tabIndex={0}
            >
              EN
            </button>
          </div>
          <a
            href="/"
            className="rounded-md px-3 py-2 text-sm text-slate-400 hover:text-white"
            tabIndex={0}
            onClick={handleCloseMenu}
          >
            {t('admin.nav.backHome')}
          </a>
          <button
            type="button"
            className="rounded-md px-3 py-2 text-left text-sm text-amber-200/90 hover:bg-slate-800"
            onClick={() => void handleLogout()}
            aria-label={t('admin.logout')}
          >
            {t('admin.logout')}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-800 bg-slate-950/95 px-4 py-3 backdrop-blur md:hidden">
          <button
            type="button"
            className="rounded-md border border-slate-700 p-2 text-slate-200 hover:bg-slate-800"
            onClick={handleToggleMenu}
            aria-label={menuOpen ? t('admin.closeMenu') : t('admin.openMenu')}
            aria-expanded={menuOpen}
            aria-controls="admin-sidebar"
            tabIndex={0}
          >
            <span className="flex h-4 w-5 flex-col justify-between" aria-hidden="true">
              <span className="block h-0.5 w-full rounded bg-current" />
              <span className="block h-0.5 w-full rounded bg-current" />
              <span className="block h-0.5 w-full rounded bg-current" />
            </span>
          </button>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-amber-400/90">{t('admin.brand')}</p>
            <p className="truncate text-xs text-slate-400">{admin?.email}</p>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-auto p-4 sm:p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
